import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { usePodeEditarParceiro } from "@/lib/use-pode-editar-parceiro";

const inputCls =
  "h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground";
const NOVO = "__novo__";

/**
 * Fornecedor, com cadastro ali mesmo.
 *
 * Quem está lançando a nota de uma compra tem o fornecedor na mão e não deveria abandonar o
 * lançamento pra ir até Cadastros — é o mesmo motivo de a Nova consulta deixar cadastrar o paciente
 * sem sair do diálogo.
 *
 * O cadastro rápido grava só o nome. Documento, endereço e contato ficam para Cadastros: pedir
 * tudo aqui transformaria "recebi 10 blocos" num formulário de cadastro, que é o oposto do ponto.
 *
 * A opção de criar só aparece pra OWNER/ADMIN, espelhando o `roleProcedure("ADMIN")` que
 * `partner.create` exige — mostrar o caminho e recusar no clique seria pior que não mostrar.
 */
export function FornecedorSelect({
  valor,
  onChange,
  className,
}: {
  valor: string;
  onChange: (partnerId: string) => void;
  className?: string;
}) {
  const utils = trpc.useUtils();
  const podeCriar = usePodeEditarParceiro();
  const [criando, setCriando] = useState(false);
  const [nome, setNome] = useState("");

  const query = trpc.partner.list.useQuery({ type: "SUPPLIER", take: 200, skip: 0 });

  const criar = trpc.partner.create.useMutation({
    onSuccess: (p: { id: string }) => {
      utils.partner.list.invalidate();
      utils.partner.countByType.invalidate();
      onChange(p.id);
      setCriando(false);
      setNome("");
      toast.success("Fornecedor cadastrado.");
    },
    onError: (e) => toast.error(e.message || "Não foi possível cadastrar o fornecedor."),
  });

  if (criando) {
    return (
      <div className={`flex gap-2 ${className ?? ""}`}>
        <input
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Nome do fornecedor"
          aria-label="Nome do novo fornecedor"
          className={inputCls}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter" && nome.trim().length >= 2) {
              e.preventDefault();
              criar.mutate({ type: "SUPPLIER", name: nome.trim(), status: "ACTIVE" });
            }
          }}
        />
        <button
          type="button"
          disabled={criar.isPending || nome.trim().length < 2}
          onClick={() => criar.mutate({ type: "SUPPLIER", name: nome.trim(), status: "ACTIVE" })}
          className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-sm font-semibold text-white disabled:opacity-50"
        >
          {criar.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Criar
        </button>
        <button
          type="button"
          onClick={() => {
            setCriando(false);
            setNome("");
          }}
          className="inline-flex h-9 shrink-0 items-center rounded-md border border-zinc-200 px-3 text-sm text-zinc-600 dark:border-border dark:text-muted-foreground"
        >
          Cancelar
        </button>
      </div>
    );
  }

  return (
    <select
      value={valor}
      onChange={(e) => (e.target.value === NOVO ? setCriando(true) : onChange(e.target.value))}
      aria-label="Fornecedor"
      className={`${inputCls} ${className ?? ""}`}
    >
      <option value="">Sem fornecedor</option>
      {(query.data?.partners ?? []).map((p: { id: string; name: string | null }) => (
        <option key={p.id} value={p.id}>
          {p.name ?? "(sem nome)"}
        </option>
      ))}
      {podeCriar && <option value={NOVO}>+ Novo fornecedor</option>}
    </select>
  );
}
